import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useAccessFolderEnterprise } from "@hooks/employeFolder";
import withNotification from "@hooks/useNotification";
import { SvgVisibleColumns, SvgImportData } from "@routes/IconeSvg";
import { FormProvider } from "react-hook-form";

// @ts-ignore
import animationData from "@assets/image/json/set_filters.json";

import ModalBackConfirmation from "@components/modals/modalConfirmations";
import BackButton from "@components/tailwindComponents/BackButton";
import Button from "@components/tailwindComponents/Button";
import CheckboxSimple from "@components/tailwindComponents/CheckboxSimple";
import DotLoader from "@components/tailwindComponents/DotLoader";
import EmptyData from "@components/tailwindComponents/EmptyData";
import UseFilterImage from "@components/tailwindComponents/UseFilterImage";
import EmployeTabFilter from "@entreprise/employeTab/components/EmployeTabFilter";
import BulkEditDataInjection from "./BulkEditDataInjection";

import ItemBulkEditContent from "./ItemBulkEditContent";
import { useSectionHolidayColumns } from "./container/JobAndSalary/SectionHolidayTableBulk";
import { useSectionPersonnalInfoColumns } from "./container/PersonnalInformations/SectionPersonnalInfoTableBulk";
import { useSectionEmailAndPhoneColumns } from "./container/PersonnalInformations/SectionEmailAndPhoneTableBulk";
import { useSectionAdressColumns } from "./container/PersonnalInformations/SectionAdressTableBulk";
import { useSectionHealthColumns } from "./container/PersonnalInformations/SectionHealthTableBulk";
import { useSectionClothesColumns } from "./container/PersonnalInformations/SectionClothesTableBulk";
import { useSectionFirstEmergencyColumns } from "./container/PersonnalInformations/SectionFirstEmergencyTableBulk";
import { useSectionSecondEmergencyColumns } from "./container/PersonnalInformations/SectionSecondEmergencyTableBulk";
import { useSectionJobColumns } from "./container/JobAndSalary/SectionJobTableBulk";
import { useSectionSecurityColumns } from "./container/JobAndSalary/SectionSecurityTableBulk";
import { useSectionInsuranceBankingColumns } from "./container/JobAndSalary/SectionInsuranceBankingTableBulk";
import { useSectionCourseAndSalaryColumns } from "./container/JobAndSalary/SectionCourseAndSalaryTableBulk";
import { useSectionAdditionalInfoColumns } from "./container/PersonnalInformations/SectionAdditionalInfoTableBulk";
import useBulkEditList from "./hooks/useBulkEditList";
import { SECTION_PERMISSIONS } from "./utils/bulkEditHelpers";

function BulkEditList({
  returnToEmployeList,
  showError,
  showInfo,
  showWarning
}) {
  const { t } = useTranslation("pages.employés.emploi&Salary");
  const { isReadOnlySection } = useAccessFolderEnterprise();
  const [isBackModalOpen, setIsBackModalOpen] = useState(false);
  const [isImportDataOpen, setIsImportDataOpen] = useState(false);
  const [isColumnsDropdownOpen, setIsColumnsDropdownOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState({});
  const [draftHiddenColumns, setDraftHiddenColumns] = useState({});
  const [hasInjectableFields, setHasInjectableFields] = useState(false);
  const hasShownSaveRecommendation = useRef(false);
  const columnsDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!columnsDropdownRef.current?.contains(event.target)) {
        setIsColumnsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    methods,
    isLoaded,
    isFiltering,
    isSaving,
    setIsSaving,
    filterData,
    filteredEmployesList,
    useTimeBank,
    allSelected,
    handleSelectAll,
    onSubmit,
    handleError,
    onFilterClicked,
    canSeeEmployeFolder,
    isNotDeniedEmployer,
    haveAccessFolderEnterprise,
    isActiveBankingInformationAndInsuranceAccess,
    employeeBulkFileAvailableSections,
    isInfoPersAndEmploiSalaryUnavailable,
    selectedEmployeesCount,
    globalMaxAdvantageTypes
  } = useBulkEditList();

  const holidayColumns = useSectionHolidayColumns(filteredEmployesList?.[0], useTimeBank);
  const personnalInfoColumns = useSectionPersonnalInfoColumns();
  const emailAndPhoneColumns = useSectionEmailAndPhoneColumns();
  const adressColumns = useSectionAdressColumns();
  const healthColumns = useSectionHealthColumns();
  const clothesColumns = useSectionClothesColumns();
  const firstEmergencyColumns = useSectionFirstEmergencyColumns();
  const secondEmergencyColumns = useSectionSecondEmergencyColumns();
  const jobColumns = useSectionJobColumns();
  const securityColumns = useSectionSecurityColumns();
  const insuranceBankingColumns = useSectionInsuranceBankingColumns();
  const courseAndSalaryColumns = useSectionCourseAndSalaryColumns(globalMaxAdvantageTypes);
  const additionalInfoColumns = useSectionAdditionalInfoColumns();

  const isSectionDisplayed = useCallback(
    (sectionKey) =>
      (filteredEmployesList || []).some(
        (employee) =>
          employee?.visibleSections?.jobSalaryHolidays?.includes(sectionKey) ||
          employee?.visibleSections?.personalInfo?.includes(sectionKey)
      ),
    [filteredEmployesList]
  );

  const availableColumns = useMemo(
    () => [
      ...(isSectionDisplayed("hoursPerDayAndBalance") ? holidayColumns : []),
      ...(isSectionDisplayed("personalInfos") ? personnalInfoColumns : []),
      ...(isSectionDisplayed("emailsAndPhones") ? emailAndPhoneColumns : []),
      ...(isSectionDisplayed("address") ? adressColumns : []),
      ...(isSectionDisplayed("healthAllergiesFoodPrefs") ? healthColumns : []),
      ...(isSectionDisplayed("clothingSize") ? clothesColumns : []),
      ...(isSectionDisplayed("firstContact") ? firstEmergencyColumns : []),
      ...(isSectionDisplayed("secondContact") ? secondEmergencyColumns : []),
      ...(isSectionDisplayed("job") ? jobColumns : []),
      ...(isSectionDisplayed("security") ? securityColumns : []),
      ...(isSectionDisplayed("salaryAndAdvantages") ? courseAndSalaryColumns : []),
      ...(isSectionDisplayed("bankingAndInsuranceInformation") ? insuranceBankingColumns : []),
      ...(isSectionDisplayed("additionalInformation") ? additionalInfoColumns : [])
    ],
    [
      isSectionDisplayed,
      holidayColumns,
      personnalInfoColumns,
      emailAndPhoneColumns,
      adressColumns,
      healthColumns,
      clothesColumns,
      firstEmergencyColumns,
      secondEmergencyColumns,
      jobColumns,
      securityColumns,
      courseAndSalaryColumns,
      insuranceBankingColumns,
      additionalInfoColumns
    ]
  );

  const visibleColumnOptions = useMemo(
    () => [
      { id: "employee", label: t("Employé"), disabled: true },
      ...availableColumns
    ],
    [t, availableColumns]
  );

  const displayedSectionsKey = [
    "hoursPerDayAndBalance",
    "personalInfos",
    "emailsAndPhones",
    "address",
    "healthAllergiesFoodPrefs",
    "clothingSize",
    "firstContact",
    "secondContact",
    "job",
    "security",
    "salaryAndAdvantages",
    "bankingAndInsuranceInformation",
    "additionalInformation"
  ]
    .filter(isSectionDisplayed)
    .join("|");

  const canInjectData =
    isLoaded &&
    !isFiltering &&
    Boolean(filterData.personalInfo?.length || filterData.jobSalaryHolidays?.length) &&
    hasInjectableFields;

  const handleInjectableFieldsChange = useCallback((hasFields) => {
    setHasInjectableFields(hasFields);
  }, []);

  useEffect(() => {
    setHiddenColumns({});
  }, [displayedSectionsKey]);

  useEffect(() => {
    if (isInfoPersAndEmploiSalaryUnavailable && !isSaving) {
      returnToEmployeList();
    }
  }, [isInfoPersAndEmploiSalaryUnavailable, isSaving, returnToEmployeList]);

  const getSelectedEmployeesCount = () =>
    (methods.getValues("employees") || []).filter(
      (employee) => employee?.selected
    ).length;

  const dirtyFieldsInEmployees = (
    methods.formState?.dirtyFields?.employees || []
  ).some((emp) => {
    if (!emp) return false;
    const { selected, ...rest } = emp;
    return Object.keys(rest).length > 0;
  });

  useEffect(() => {
    const shouldShowSaveRecommendation =
      dirtyFieldsInEmployees && selectedEmployeesCount >= 10;

    if (shouldShowSaveRecommendation && !hasShownSaveRecommendation.current) {
      showInfo("bulk_edit_save_recommended");
      hasShownSaveRecommendation.current = true;
    }

    if (!shouldShowSaveRecommendation) {
      hasShownSaveRecommendation.current = false;
    }
  }, [dirtyFieldsInEmployees, selectedEmployeesCount, showInfo]);

  const isSaveDisabled = useMemo(() => {
    if (!canSeeEmployeFolder) return true;

    const unLockedEmployees = filteredEmployesList.some((e) =>
      isNotDeniedEmployer(e._id)
    );
    if (!unLockedEmployees) return true;

    const hasFullAccessEmployees = filteredEmployesList.some((e) =>
      haveAccessFolderEnterprise(e._id)
    );
    if (!hasFullAccessEmployees) return true;

    if (!filterData.personalInfo && !filterData.jobSalaryHolidays) return true;

    const deniedPersonalInformation = filterData.personalInfo?.some((section) =>
      isReadOnlySection(SECTION_PERMISSIONS[section])
    );

    const deniedJobAndSalary = filterData.jobSalaryHolidays?.some((section) =>
      isReadOnlySection(SECTION_PERMISSIONS[section])
    );

    const hasSelectedEmployee = selectedEmployeesCount > 0;

    return (
      !hasSelectedEmployee ||
      deniedPersonalInformation ||
      deniedJobAndSalary ||
      isSaving ||
      (filterData?.jobSalaryHolidays?.includes(
        "bankingAndInsuranceInformation"
      ) &&
        !isActiveBankingInformationAndInsuranceAccess)
    );
  }, [
    canSeeEmployeFolder,
    isNotDeniedEmployer,
    haveAccessFolderEnterprise,
    filterData,
    isSaving,
    isActiveBankingInformationAndInsuranceAccess,
    filteredEmployesList,
    selectedEmployeesCount
  ]);

  const handleSubmit = async () => {
    setIsSaving(true);
    const isSaved = await new Promise((resolve) => {
      methods.handleSubmit(
        async (data) => {
          resolve(Boolean(await onSubmit(data)));
        },
        (errors) => {
          handleError(errors);
          resolve(false);
        }
      )();
    });

    if (isSaved) {
      hasShownSaveRecommendation.current = false;
    }

    return isSaved;
  };

  const handleBackClick = () => {
    if (getSelectedEmployeesCount()) {
      setIsBackModalOpen(true);
      return;
    }

    returnToEmployeList();
  };

  const handleCloseBackModal = () => {
    setIsBackModalOpen(false);
    returnToEmployeList();
  };

  const handleModalBack = () => {
    setIsBackModalOpen(false);
  };

  const handleSaveAndBack = async () => {
    const isSaved = await handleSubmit();

    if (isSaved) {
      setIsBackModalOpen(false);
      returnToEmployeList();
    }
  };

  const handleShowColumns = () => {
    if (!isColumnsDropdownOpen) {
      setDraftHiddenColumns(hiddenColumns);
    }
    setIsColumnsDropdownOpen((isOpen) => !isOpen);
  };

  const handleColumnToggle = (columnId) => {
    setDraftHiddenColumns((columns) => ({
      ...columns,
      [columnId]: !columns[columnId]
    }));
  };

  const handleColumnsSave = () => {
    setHiddenColumns(draftHiddenColumns);
    setIsColumnsDropdownOpen(false);
  };

  return (
    <FormProvider {...methods}>
      <div className="arh-relative arh-bg-white arh-p-4 arh-px-9">
        <EmployeTabFilter
          onFilter={onFilterClicked}
          defaultValues={filterData}
          defaultOpen={true}
          disabled={isSaving}
          employeeBulkFileAvailableSections={employeeBulkFileAvailableSections}
          withPostes
          withHierarchical
        />
        <div className="arh-flex arh-items-center arh-justify-between arh-p-4">
          <BackButton
            onClick={handleBackClick}
            wait={isSaving}
          />
          <div className="arh-flex arh-items-center arh-gap-3">
            <div
              ref={columnsDropdownRef}
              className="arh-relative"
            >
              <Button
                className="desktop-l:!arh-w-fit"
                variant="green-outlined"
                icon={<SvgVisibleColumns color="#167462" />}
                onClick={handleShowColumns}
                type="button"
                aria-expanded={isColumnsDropdownOpen}
                aria-haspopup="true"
              >
                {t("Colonnes visibles")}
              </Button>
              {isColumnsDropdownOpen && (
                <div className="arh-absolute arh-left-0 arh-z-20 arh-mt-1 arh-w-[190px] arh-rounded-md arh-bg-white arh-p-2 arh-shadow-xl">
                  <div className="arh-flex arh-w-full arh-flex-col arh-overflow-hidden">
                    {visibleColumnOptions.map((column) => (
                      <div
                        key={column.id}
                        className={`arh-w-full arh-rounded arh-p-0 arh-overflow-hidden ${column.disabled || !draftHiddenColumns[column.id] ? "arh-bg-[#F1F5F9]" : ""}`}
                      >
                        <CheckboxSimple
                          label={column.label}
                          checked={column.disabled ? true : !draftHiddenColumns[column.id]}
                          disabled={column.disabled}
                          onClick={() => {
                            if (!column.disabled) handleColumnToggle(column.id);
                          }}
                          haveHover={false}
                          padding="arh-p-2"
                          customClass="arh-ml-2 arh-truncate arh-whitespace-nowrap arh-overflow-hidden"
                          TooltipLabel={true}
                        />
                      </div>
                    ))}
                  </div>
                  <Button
                    className="!arh-w-full arh-mt-2"
                    variant="green-outlined"
                    onClick={handleColumnsSave}
                    type="button"
                  >
                    {t("Enregistrer")}
                  </Button>
                </div>
              )}
            </div>
            <div className="arh-relative" data-import-data-button>
              <Button
                className="desktop-l:!arh-w-fit"
                variant={canInjectData ? "green-outlined" : "grey"}
                icon={<SvgImportData color={canInjectData ? "#167462" : "#A8A8A8"} />}
                onClick={() => setIsImportDataOpen((isOpen) => !isOpen)}
                disabled={!canInjectData}
                type="button"
              >
                {t("Injecter données")}
              </Button>
            </div>
            <Button
              className="!arh-w-[250px]"
              onClick={handleSubmit}
              wait={isSaving}
              disabled={isSaveDisabled}
              type="button"
            >
              {selectedEmployeesCount
                ? `${t("Enregistrer")} (${selectedEmployeesCount})`
                : t("Enregistrer")}
            </Button>
          </div>
        </div>
        <div className="arh-relative">
          <BulkEditDataInjection
            isOpen={isImportDataOpen}
            onClose={() => setIsImportDataOpen(false)}
            selectedItemNumber={selectedEmployeesCount}
            maxAdvantageTypes={globalMaxAdvantageTypes}
            isSaving={isSaving}
            hasBankingAccess={isActiveBankingInformationAndInsuranceAccess}
            isSectionDisplayed={isSectionDisplayed}
            hiddenColumns={hiddenColumns}
            title={t("Injecter")}
            onInjectableFieldsChange={handleInjectableFieldsChange}
          />
        </div>
        <div className="arh-min-h-[60dvh]">
          {!isLoaded || isFiltering ? (
            <div className="arh-flex arh-h-[20dvh] arh-items-center arh-justify-center">
              <DotLoader size="small" />
            </div>
          ) : !(
              filterData.personalInfo?.length ||
              filterData.jobSalaryHolidays?.length
            ) ? (
            <UseFilterImage animationData={animationData}>
              {t("Veuillez sélectionner les employés")} <br />
              {t("et les informations à modifier")}
            </UseFilterImage>
          ) : !filteredEmployesList?.length ? (
            <EmptyData>{t("Aucun résultat à afficher")}</EmptyData>
          ) : (
            <ItemBulkEditContent
              t={t}
              employees={filteredEmployesList}
              useTimeBank={useTimeBank}
              mustWait={isSaving}
              control={methods.control}
              getValues={methods.getValues}
              trigger={methods.trigger}
              formState={methods.formState}
              onSelectAll={handleSelectAll}
              allSelected={allSelected}
              setValue={methods.setValue}
              canSeeEmployeFolder={canSeeEmployeFolder}
              isNotDeniedEmployer={isNotDeniedEmployer}
              haveAccessFolderEnterprise={haveAccessFolderEnterprise}
              showError={showError}
              showWarning={showWarning}
              isActiveBankingInformationAndInsuranceAccess={
                isActiveBankingInformationAndInsuranceAccess
              }
              watch={methods.watch}
              globalMaxAdvantageTypes={globalMaxAdvantageTypes}
              hiddenColumns={hiddenColumns}
            />
          )}
        </div>
        <ModalBackConfirmation
          isOpen={isBackModalOpen}
          content={t("Êtes-vous sûr de vouloir quitter sans enregistrer ?")}
          onCloseModal={handleCloseBackModal}
          handleSave={handleSaveAndBack}
          onBack={handleModalBack}
          isLoading={isSaving}
          wait={isSaving}
          withCloseCroix={false}
        />
      </div>
    </FormProvider>
  );
}

export default withNotification(BulkEditList);
